// frontend/src/pages/Groups/GroupManagement.jsx
// Legal CRM is the sole writer of Group (see
// docs/adr/0002-entity-group-brand-single-writer.md in HR Apps V2). Writes
// here push to HR — its own Group page is read-only.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../hooks/useAuth";
import {
  getGroups,
  createGroup,
  updateGroup,
  deleteGroup,
  assignEntitiesToGroup,
} from "../../api/group.api.js";
import { getEntities } from "../../api/entity.api.js";
import { PageHeader, Spinner, EmptyState } from "../../components/ui";
import MultiSelect from "../../components/MultiSelect.jsx";
import { Plus, Pencil, Trash2, Users, X, Save, ChevronDown, ChevronUp } from "lucide-react";

// ─── Create/edit modal ──────────────────────────────────────────────────────

function GroupFormModal({ group, onClose, onSaved }) {
  const isEdit = !!group;
  const [form, setForm] = useState({
    name: group?.name ?? "",
    code: group?.code ?? "",
    description: group?.description ?? "",
    color: group?.color ?? "#6366F1",
  });
  const [error, setError] = useState(null);

  const save = useMutation({
    mutationFn: () =>
      isEdit ? updateGroup(group.id, form) : createGroup(form),
    onSuccess: (saved) => {
      onSaved(saved);
      onClose();
    },
    onError: (err) => setError(err?.response?.data?.error ?? err.message),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">{isEdit ? "Edit Group" : "New Group"}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <div>
            <label className="label text-xs">Name <span className="text-red-400">*</span></label>
            <input
              className="input text-sm"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              autoFocus
            />
          </div>

          <div>
            <label className="label text-xs">Code <span className="text-gray-400 font-normal">(optional)</span></label>
            <input
              className="input text-sm font-mono"
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
            />
          </div>

          <div>
            <label className="label text-xs">Description <span className="text-gray-400 font-normal">(optional)</span></label>
            <textarea
              className="input text-sm"
              rows={2}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </div>

          <div>
            <label className="label text-xs">Color</label>
            <input
              type="color"
              className="h-9 w-16 rounded border border-gray-300"
              value={form.color}
              onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button
            onClick={() => { setError(null); save.mutate(); }}
            disabled={save.isPending || !form.name.trim()}
            className="btn-primary flex items-center gap-2 text-sm"
          >
            <Save size={14} /> {save.isPending ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Assign entities modal ──────────────────────────────────────────────────

function AssignEntitiesModal({ group, onClose, onSaved }) {
  const { data: entities = [] } = useQuery({
    queryKey: ["entities"],
    queryFn: () => getEntities(),
  });
  const [selected, setSelected] = useState(group.entities.map((e) => e.id));
  const [error, setError] = useState(null);

  const save = useMutation({
    mutationFn: () => assignEntitiesToGroup(group.id, selected),
    onSuccess: (saved) => {
      onSaved(saved);
      onClose();
    },
    onError: (err) => setError(err?.response?.data?.error ?? err.message),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">Assign Entities — {group.name}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5">
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3">
              {error}
            </div>
          )}
          <MultiSelect
            label="Entity"
            options={entities}
            selected={selected}
            onChange={setSelected}
            getLabel={(e) => `${e.code ? e.code + " — " : ""}${e.name}`}
          />
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button
            onClick={() => { setError(null); save.mutate(); }}
            disabled={save.isPending}
            className="btn-primary flex items-center gap-2 text-sm"
          >
            <Save size={14} /> {save.isPending ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Group card ──────────────────────────────────────────────────────────────

function GroupCard({ group, isMaster, onEdit, onAssign, onDelete }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="card p-5">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <span
            className="w-3 h-3 rounded-full flex-shrink-0"
            style={{ backgroundColor: group.color || "#9CA3AF" }}
          />
          <div>
            <h3 className="font-semibold text-gray-900">{group.name}</h3>
            {group.code && <p className="text-xs text-gray-400 font-mono">{group.code}</p>}
          </div>
        </div>
        {isMaster && (
          <div className="flex items-center gap-1">
            <button onClick={() => onEdit(group)} className="text-gray-400 hover:text-brand-500 p-1" title="Edit">
              <Pencil size={14} />
            </button>
            <button onClick={() => onAssign(group)} className="text-gray-400 hover:text-brand-500 p-1" title="Assign entities">
              <Users size={14} />
            </button>
            <button onClick={() => onDelete(group)} className="text-gray-400 hover:text-red-500 p-1" title="Delete">
              <Trash2 size={14} />
            </button>
          </div>
        )}
      </div>

      {group.description && (
        <p className="text-sm text-gray-500 mt-2">{group.description}</p>
      )}

      <button
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 mt-3"
      >
        {group._count?.entities ?? group.entities.length} {group._count?.entities === 1 ? "entity" : "entities"}
        {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>

      {expanded && (
        <ul className="mt-2 space-y-1 border-t border-gray-100 pt-2">
          {group.entities.length === 0 ? (
            <li className="text-xs text-gray-400 italic">No entities assigned</li>
          ) : (
            group.entities.map((e) => (
              <li key={e.id} className="text-xs text-gray-600 font-mono">
                {e.code ? `${e.code} — ` : ""}{e.name}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export default function GroupManagement() {
  const { user } = useAuth();
  const isMaster = user?.userType === "admin" && user?.role === "MASTER";
  const qc = useQueryClient();

  const { data: groups, isLoading } = useQuery({
    queryKey: ["groups"],
    queryFn: getGroups,
  });

  const [formGroup, setFormGroup] = useState(undefined); // undefined = closed, null = create, group = edit
  const [assignGroup, setAssignGroup] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  const remove = useMutation({
    mutationFn: (id) => deleteGroup(id),
    onSuccess: () => {
      setDeleteError(null);
      qc.invalidateQueries({ queryKey: ["groups"] });
    },
    onError: (err) => setDeleteError(err?.response?.data?.error ?? err.message),
  });

  function refresh() {
    qc.invalidateQueries({ queryKey: ["groups"] });
  }

  function handleDelete(group) {
    if (!confirm(`Delete group "${group.name}"?`)) return;
    remove.mutate(group.id);
  }

  if (isLoading) return <Spinner className="mt-20 mx-auto" />;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <PageHeader
        title="Groups"
        subtitle="Corporate group structure — entities are assigned here"
        action={
          isMaster && (
            <button onClick={() => setFormGroup(null)} className="btn-primary flex items-center gap-2 text-sm">
              <Plus size={14} /> New Group
            </button>
          )
        }
      />

      {deleteError && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">
          {deleteError}
        </div>
      )}

      {!groups || groups.length === 0 ? (
        <EmptyState title="No groups yet" description="Create a group to start organizing entities." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map((group) => (
            <GroupCard
              key={group.id}
              group={group}
              isMaster={isMaster}
              onEdit={setFormGroup}
              onAssign={setAssignGroup}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {formGroup !== undefined && (
        <GroupFormModal
          group={formGroup}
          onClose={() => setFormGroup(undefined)}
          onSaved={refresh}
        />
      )}

      {assignGroup && (
        <AssignEntitiesModal
          group={assignGroup}
          onClose={() => setAssignGroup(null)}
          onSaved={refresh}
        />
      )}
    </div>
  );
}
